'use client'

import * as React from 'react'
import * as Popover from '@radix-ui/react-popover'
import { ChevronDown, MapPin, Home, Check, Loader2, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSearchStore } from '@/lib/store'
import type { PropertyType, Location } from '@/lib/types'
import { api } from '@/lib/api'
import { useI18n } from '@/lib/i18n'

interface LocationDropdownsProps {
  className?: string
}

export function LocationDropdowns({ className }: LocationDropdownsProps) {
  const { t } = useI18n()
  const { 
    filters, 
    updateFilter, 
    selectedCity, 
    setSelectedCity, 
    selectedDistricts, 
    setSelectedDistricts 
  } = useSearchStore()

  // State for Property Type dropdown
  const [typeDropdownOpen, setTypeDropdownOpen] = React.useState(false)

  // State for City dropdown
  const [cityDropdownOpen, setCityDropdownOpen] = React.useState(false)
  const [cities, setCities] = React.useState<string[]>([])
  const [citiesLoading, setCitiesLoading] = React.useState(true)
  const [citySearchQuery, setCitySearchQuery] = React.useState('')
  const cityInputRef = React.useRef<HTMLInputElement>(null)

  const filteredCities = React.useMemo(() => {
    if (!citySearchQuery.trim()) return cities
    const needle = citySearchQuery.trim().toLowerCase()
    return cities.filter((c) => c.toLowerCase().includes(needle))
  }, [cities, citySearchQuery])

  // State for District dropdown
  const [districtDropdownOpen, setDistrictDropdownOpen] = React.useState(false)
  const [districtSearchQuery, setDistrictSearchQuery] = React.useState('')
  const [locations, setLocations] = React.useState<Location[]>([])
  const [districtsLoading, setDistrictsLoading] = React.useState(false)
  const debounceRef = React.useRef<NodeJS.Timeout>(null)

  // Fetch cities on mount
  React.useEffect(() => {
    const fetchCities = async () => {
      try {
        const data = await api.getCities()
        setCities(data)
      } catch (error) {
        console.error('Failed to fetch cities:', error)
        setCities(['Praha']) // Fallback
      } finally {
        setCitiesLoading(false)
      }
    }
    fetchCities()
  }, [])

  // Fetch locations for districts when search query changes
  React.useEffect(() => {
    if (!districtDropdownOpen || !districtSearchQuery.trim()) {
      setLocations([])
      return
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(async () => {
      setDistrictsLoading(true)
      try {
        const results = await api.getLocations(selectedCity, districtSearchQuery, 15)
        setLocations(results)
      } catch (error) {
        console.error('Failed to fetch locations:', error)
        setLocations([])
      } finally {
        setDistrictsLoading(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [districtDropdownOpen, districtSearchQuery, selectedCity])

  const toggleDistrict = (district: string) => {
    if (selectedDistricts.includes(district)) {
      setSelectedDistricts(selectedDistricts.filter(d => d !== district))
    } else {
      setSelectedDistricts([...selectedDistricts, district])
    }
  }

  const removeDistrict = (district: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedDistricts(selectedDistricts.filter(d => d !== district))
  }

  return (
    <div className={cn('flex items-center gap-2 flex-wrap', className)}>
      {/* Property Type Dropdown */}
      <Popover.Root open={typeDropdownOpen} onOpenChange={setTypeDropdownOpen}>
        <Popover.Trigger asChild>
          <button
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium',
              'bg-primary/10 text-primary hover:bg-primary/20 transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary/30'
            )}
          >
            <Home className="w-3.5 h-3.5" />
            <span className="capitalize">{filters.type ? t(`search.${filters.type}`, filters.type) : t('search.apartment', 'apartment')}</span>
            <ChevronDown className={cn(
              'w-3.5 h-3.5 transition-transform',
              typeDropdownOpen && 'rotate-180'
            )} />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="z-50 min-w-[140px] rounded-xl bg-background border border-border shadow-lg elevation-md overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2"
            sideOffset={5}
            align="start"
          >
            {(['apartment', 'room', 'house'] as PropertyType[]).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => {
                  updateFilter('type', type)
                  setTypeDropdownOpen(false)
                }}
                className={cn(
                  'w-full px-4 py-2.5 text-left text-sm capitalize',
                  'hover:bg-primary/10 transition-colors',
                  (filters.type || 'apartment') === type
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-foreground'
                )}
              >
                {t(`search.${type}`, type)}
              </button>
            ))}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* City Dropdown */}
      <Popover.Root open={cityDropdownOpen} onOpenChange={(open) => {
        setCityDropdownOpen(open)
        if (!open) setCitySearchQuery('')
        else setTimeout(() => cityInputRef.current?.focus(), 100)
      }}>
        <Popover.Trigger asChild>
          <button
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium',
              'bg-primary/10 text-primary hover:bg-primary/20 transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary/30'
            )}
            disabled={citiesLoading}
          >
            <MapPin className="w-3.5 h-3.5" />
            <span>{selectedCity || t('search.select', 'Select City')}</span>
            <ChevronDown className={cn(
              'w-3.5 h-3.5 transition-transform',
              cityDropdownOpen && 'rotate-180'
            )} />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="z-50 w-[220px] rounded-lg bg-popover border border-border shadow-lg overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2"
            sideOffset={5}
            align="start"
          >
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  ref={cityInputRef}
                  type="text"
                  value={citySearchQuery}
                  onChange={(e) => setCitySearchQuery(e.target.value)}
                  placeholder={t('search.searchCities', 'Search cities...')}
                  className={cn(
                    'w-full pl-8 pr-3 py-1.5 text-sm rounded-md',
                    'bg-muted/50 border border-border',
                    'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary'
                  )}
                />
              </div>
            </div>
            <div className="max-h-[240px] overflow-y-auto">
              {filteredCities.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                  {t('search.noCitiesFound', 'No cities found')}
                </div>
              ) : (
                filteredCities.map((city) => (
                  <button
                    key={city}
                    type="button"
                    onClick={() => {
                      setSelectedCity(city)
                      setCityDropdownOpen(false)
                      setCitySearchQuery('')
                      setSelectedDistricts([])
                    }}
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm flex items-center justify-between',
                      'hover:bg-accent transition-colors',
                      selectedCity === city && 'bg-primary/10 text-primary font-medium'
                    )}
                  >
                    <span>{city}</span>
                    {selectedCity === city && <Check className="w-4 h-4" />}
                  </button>
                ))
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* District Dropdown */}
      <Popover.Root open={districtDropdownOpen} onOpenChange={setDistrictDropdownOpen}>
        <Popover.Trigger asChild>
          <button
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium',
              'bg-primary/10 text-primary hover:bg-primary/20 transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary/30',
              selectedDistricts.length > 0 && 'pr-2'
            )}
          >
            <MapPin className="w-3.5 h-3.5" />
            {selectedDistricts.length === 0 ? (
              <span>{t('search.district', 'District')}</span>
            ) : (
              <span className="inline-flex items-center gap-1 flex-wrap">
                {selectedDistricts.slice(0, 2).map((district) => (
                  <span
                    key={district}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-primary/20 text-primary text-xs font-medium"
                  >
                    {district}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => removeDistrict(district, e)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          removeDistrict(district, e as unknown as React.MouseEvent)
                        }
                      }}
                      className="ml-0.5 hover:bg-primary/30 rounded-full p-0.5 cursor-pointer"
                  aria-label={`${t('search.removeFilter', 'Remove')} ${district}`}
                    >
                      <X className="w-2.5 h-2.5" />
                    </span>
                  </span>
                ))}
                {selectedDistricts.length > 2 && (
                  <span className="text-xs text-primary/70">+{selectedDistricts.length - 2}</span>
                )}
              </span>
            )}
            <ChevronDown className={cn(
              'w-3.5 h-3.5 transition-transform flex-shrink-0',
              districtDropdownOpen && 'rotate-180'
            )} />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="z-50 w-[280px] rounded-lg bg-popover border border-border shadow-lg overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2"
            sideOffset={5}
            align="start"
          >
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  value={districtSearchQuery}
                  onChange={(e) => setDistrictSearchQuery(e.target.value)}
                  placeholder={t('search.searchDistricts', 'Search districts...')}
                  className={cn(
                    'w-full pl-8 pr-3 py-1.5 text-sm rounded-md',
                    'bg-muted/50 border border-border',
                    'focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary'
                  )}
                />
              </div>
            </div>

            <div className="max-h-[240px] overflow-y-auto">
              {!districtSearchQuery.trim() ? (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                  {t('search.typeToSearchDistricts', 'Type to search districts...')}
                </div>
              ) : districtsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
                </div>
              ) : locations.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                  {t('search.noDistrictsFound', 'No districts found')}
                </div>
              ) : (
                locations.map((location, index) => {
                  let nameValue = location.name
                  if (typeof nameValue === 'object' && nameValue !== null) {
                    const input = (nameValue as { input?: string | string[] }).input
                    nameValue = Array.isArray(input) ? input[0] : input || ''
                  }
                  const displayName = String(nameValue || location.district)
                  const isSelected = selectedDistricts.includes(displayName)

                  return (
                    <button
                      key={`${displayName}-${index}`}
                      type="button"
                      onClick={() => toggleDistrict(displayName)}
                      className={cn(
                        'w-full flex items-center gap-2 px-3 py-2 text-sm text-left',
                        'hover:bg-accent transition-colors',
                        isSelected && 'bg-primary/5'
                      )}
                    >
                      <div className={cn(
                        'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                        isSelected
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'border-border'
                      )}>
                        {isSelected && <Check className="w-3 h-3" />}
                      </div>
                      <span className={cn(
                        'flex-1 truncate',
                        isSelected && 'font-medium text-primary'
                      )}>
                        {displayName}
                      </span>
                      {location.type && location.type !== 'district' && (
                        <span className="text-xs text-muted-foreground">
                          {location.type}
                        </span>
                      )}
                    </button>
                  )
                })
              )}
            </div>

            {selectedDistricts.length > 0 && (
              <div className="p-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => setSelectedDistricts([])}
                  className="w-full px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('search.clearAll', 'Clear all')} ({selectedDistricts.length})
                </button>
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  )
}

